(() => {
  'use strict';
  const contact = document.querySelector('[data-lu-inbox-contact]');
  if (!contact || contact.dataset.inboxReady) return;
  contact.dataset.inboxReady = 'true';
  const trigger = contact.querySelector('[data-lu-inbox-open]');
  const status = contact.querySelector('[data-lu-inbox-status]');
  const hours = contact.querySelector('[data-lu-inbox-hours]');
  const root = document.documentElement;
  let widget, panel, observer, opening = false, active = false, sequence = 0;
  let inertElements = [];
  let eventController, previousMode, modeChanged = false, loading;
  const readyWidgets = new WeakSet();

  function hideLoading() {
    if (loading) loading.hidden = true;
    panel?.removeAttribute('aria-busy');
  }

  function showLoading() {
    if (!loading) {
      loading = document.createElement('div');
      loading.className = 'lu-inbox-loading';
      loading.setAttribute('role', 'status');
      loading.setAttribute('aria-live', 'polite');
      loading.innerHTML = '<span class="lu-inbox-loading__mark" aria-hidden="true"></span><p>Opening your conversation…</p><span>You can leave a message outside support hours, too.</span>';
      document.body.appendChild(loading);
    }
    loading.hidden = false;
    panel.setAttribute('aria-busy', 'true');
    viewport();
  }

  function updateHours() {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles', weekday: 'short', hour: 'numeric', hourCycle: 'h23'
      }).formatToParts(new Date());
      const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
      const withinHours = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(value.weekday)
        && Number(value.hour) >= 9 && Number(value.hour) < 17;
      hours.textContent = withinHours
        ? 'It is currently within our support hours. You can also leave a message.'
        : 'Currently outside support hours. Leave a message for our team.';
    } catch (_) { hours.textContent = ''; }
  }

  // Read the actual Inbox component's open shadow roots, without sending a message.
  function findDeep(node, selector) {
    const found = node.querySelector(selector);
    if (found) return found;
    for (const element of node.querySelectorAll('*')) {
      if (element.shadowRoot) {
        const nested = findDeep(element.shadowRoot, selector);
        if (nested) return nested;
      }
    }
    return null;
  }

  function visible(element) {
    return element.getClientRects().length > 0
      && getComputedStyle(element).visibility !== 'hidden'
      && !element.closest('[inert], [hidden]');
  }

  function focusables(node, result = []) {
    for (const element of node.children || []) {
      const style = getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || element.hidden || element.inert) continue;
      if (element.matches('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), iframe, [tabindex]')
        && element.tabIndex >= 0 && visible(element)) result.push(element);
      if (element.shadowRoot) focusables(element.shadowRoot, result);
      else focusables(element, result);
    }
    return result;
  }

  function deepActive() {
    let element = document.activeElement;
    while (element?.shadowRoot?.activeElement) element = element.shadowRoot.activeElement;
    return element;
  }

  function viewport() {
    if (!active || !widget) return;
    const height = window.visualViewport?.height || window.innerHeight;
    const offset = window.visualViewport?.offsetTop || 0;
    widget.style.setProperty('--lu-chat-height', `${height}px`);
    widget.style.setProperty('--lu-chat-top', `${offset + height / 2}px`);
    if (panel) panel.setAttribute('aria-modal', 'true');
    if (panel && loading && !loading.hidden) {
      const box = panel.getBoundingClientRect();
      Object.assign(loading.style, { left: `${box.left}px`, top: `${box.top + 60}px`, width: `${box.width}px`, height: `${Math.max(0, box.height - 60)}px` });
    }
  }

  function isolatePage() {
    // Preserve any existing inert state. Do not move the Shopify component.
    let branch = widget;
    while (branch && branch !== document.body) {
      for (const sibling of branch.parentElement?.children || []) {
        if (sibling === branch || sibling === loading || sibling.inert || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) continue;
        sibling.inert = true;
        inertElements.push(sibling);
      }
      branch = branch.parentElement;
    }
  }

  function close(restoreFocus = true) {
    sequence += 1;
    active = opening = false;
    eventController?.abort();
    observer?.disconnect();
    observer = null;
    try { if (widget && typeof widget.close === 'function') widget.close(); } catch (_) {}
    if (widget && modeChanged) {
      if (previousMode === null) widget.removeAttribute('mode');
      else widget.setAttribute('mode', previousMode);
      modeChanged = false;
    }
    hideLoading();
    root.classList.remove('lu-inbox-active');
    inertElements.forEach(element => { element.inert = false; });
    inertElements = [];
    trigger.setAttribute('aria-expanded', 'false');
    trigger.removeAttribute('aria-busy');
    trigger.disabled = false;
    if (restoreFocus) trigger.focus({ preventScroll: true });
    panel = null;
  }

  function waitFor(read, run, timeout = 10000) {
    const started = performance.now();
    return new Promise((resolve, reject) => {
      function check() {
        if (run !== sequence) { reject(new Error('cancelled')); return; }
        const value = read();
        if (value) { resolve(value); return; }
        if (performance.now() - started >= timeout) { reject(new Error('unavailable')); return; }
        window.setTimeout(check, 100);
      }
      check();
    });
  }

  async function open() {
    if (opening || active) return;
    const run = ++sequence;
    opening = true;
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    status.textContent = 'Opening chat…';
    updateHours();
    try {
      widget = await waitFor(() => {
        const element = document.querySelector('shopify-chat');
        return element && typeof element.show === 'function' && typeof element.close === 'function' ? element : null;
      }, run);
      active = true;
      let contentReady = readyWidgets.has(widget);
      eventController = new AbortController();
      for (const type of ['shopify-chat:empty-state', 'shopify-chat:populated', 'shopify-chat:restored']) {
        widget.addEventListener(type, event => {
          if (!event.composedPath().includes(widget)) return;
          if (type === 'shopify-chat:empty-state' && event.detail?.visible !== true) return;
          contentReady = true;
        }, { signal: eventController.signal });
      }
      // Managed mode is the installed component's embedded layout. It also relays
      // Escape from its verified cross-origin iframe through a composed key event.
      previousMode = widget.getAttribute('mode');
      modeChanged = true;
      widget.setAttribute('mode', 'managed');
      root.classList.add('lu-inbox-active');
      viewport();
      // These methods and exported CSS parts were verified in the installed Inbox runtime.
      widget.show();
      panel = await waitFor(() => {
        const element = widget.shadowRoot && findDeep(widget.shadowRoot, '[part~="dialog"][role="dialog"]');
        return element && visible(element) && !element.inert ? element : null;
      }, run);
      opening = false;
      trigger.disabled = false;
      trigger.removeAttribute('aria-busy');
      trigger.setAttribute('aria-expanded', 'true');
      status.textContent = 'Opening chat…';
      panel.setAttribute('aria-label', 'Chat with LUULEY');
      panel.removeAttribute('aria-labelledby');
      viewport();
      if (!contentReady) showLoading();
      isolatePage();
      panel.focus({ preventScroll: true });
      observer = new MutationObserver(() => {
        if (active && !widget.open && !widget.hasAttribute('open')) close();
      });
      observer.observe(widget, { attributes: true, attributeFilter: ['open'] });
      await waitFor(() => contentReady && widget.hasAttribute('shell-ready'), run, 20000);
      readyWidgets.add(widget);
      hideLoading();
      status.textContent = '';
    } catch (error) {
      if (run !== sequence) return;
      close(false);
      status.textContent = 'Chat is unavailable right now. Please use the message form below or email info@luuley.com.';
      trigger.focus({ preventScroll: true });
    }
  }

  trigger.hidden = false;
  trigger.addEventListener('click', open);
  document.addEventListener('keydown', event => {
    if (!active && !opening) return;
    if (!event.isTrusted && !event.composedPath().includes(widget)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
      status.textContent = '';
    } else if (event.key === 'Tab' && panel) {
      const targets = focusables(panel);
      const current = deepActive();
      const index = targets.indexOf(current);
      if (!targets.length || (event.shiftKey && index <= 0) || (!event.shiftKey && (index === -1 || index === targets.length - 1))) {
        event.preventDefault();
        (targets.length ? targets[event.shiftKey ? targets.length - 1 : 0] : panel).focus();
      }
    }
  }, true);
  document.addEventListener('click', event => {
    if (!active || opening || !panel) return;
    const path = event.composedPath();
    if (path.includes(widget) && !path.includes(panel)
      && !path.some(element => element instanceof HTMLElement && element.hasAttribute('popover'))) close();
  });
  document.addEventListener('focusin', event => {
    if (active && !opening && panel && !event.composedPath().includes(widget)) panel.focus({ preventScroll: true });
  });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) updateHours(); });
  window.addEventListener('resize', viewport);
  window.visualViewport?.addEventListener('resize', viewport);
  window.visualViewport?.addEventListener('scroll', viewport);
  window.addEventListener('pagehide', () => { if (active || opening) close(false); });
  updateHours();
  window.setInterval(updateHours, 60000);
})();
